import { FARM_DIAGNOSTICS_PRACTICE_LABELS } from 'constants/uiConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import { InputTaskCategory } from 'interfaces/IInputTask';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/Store';
import type { SummaryEntityCategory } from '../../../../redux/slices/InsightsSlice';
import {
  classifyTrend,
  computeFinancialWue,
  computeGrossMarginPerHa,
  computeRollingAverage3yr,
  computeWueIndex,
  FARM_DIAGNOSTICS_COST_CATEGORY_ORDER,
  resolveYieldKgPerHa,
  type RollingAveragePoint,
  type TrendDirection,
} from './computeFarmDiagnosticsMetrics';
import type { WueTrendYearRow } from './computeWueTrend';

export interface FarmDiagnosticsYearSeries {
  year: number;
  areaHa: number;
  /** Effective (post yieldOverride) yield, per ha, in `yieldUnit`. */
  yieldValue: number;
  yieldUnit: string;
  pricePerUnit: number;
  /** Effective (post costOverrides) $/ha per category. */
  costsPerHa: Partial<Record<SummaryEntityCategory, number>>;
  variableCostPerHa: number;
  gmPerHa: number;
  rainfallMm: number | null;
  /** Human-readable labels (e.g. "Conventional till"), already mapped from the raw PracticeTag enum keys stored in Redux — consumers should never need FARM_DIAGNOSTICS_PRACTICE_LABELS themselves. */
  practiceTags: string[];
  /** Null when `yieldUnit` isn't a recognised mass unit — WUE Index can't be computed for this year. */
  yieldKgPerHa: number | null;
  /** Null when rainfall hasn't been entered for this year, or yield couldn't be converted to kg. */
  wueIndex: number | null;
  /** Null when rainfall hasn't been entered for this year. */
  financialWue: number | null;
}

export interface FarmDiagnosticsSeries {
  years: FarmDiagnosticsYearSeries[];
  wueRollingAvg: RollingAveragePoint[];
  financialWueRollingAvg: RollingAveragePoint[];
  wueTrendByYear: Record<number, TrendDirection | null>;
  financialWueTrendByYear: Record<number, TrendDirection | null>;
}

/**
 * Turns Epic 1's per-year rows into the full derived series the four Farm
 * Diagnostics lenses share — applying the Overview table's own per-row
 * Plan/Actual selection and its session-only yield/cost overrides, joining
 * in farm-level rainfall/practice tags, and computing WUE Index / Financial
 * WUE / rolling averages / trend classification. The Overview table is the
 * single source of truth this hook reads from — whatever's selected/edited
 * there is what every chart in every lens shows.
 *
 * Selectors are scoped to just this paddock's/farm's own override slice so
 * a keystroke on one paddock's override doesn't re-render every other
 * paddock's derived series; the surrounding useMemo means the expensive
 * per-year math only re-runs when those slices (or the rows/gUnits/selection)
 * change, not on every unrelated re-render.
 */
export const useFarmDiagnosticsSeries = (
  rows: WueTrendYearRow[],
  paddockId: string | undefined,
  farmId: string | undefined,
  gUnits: GUnit[],
  selectedCategory: Record<number, InputTaskCategory>
): FarmDiagnosticsSeries => {
  const overridesForPaddock = useSelector((state: RootState) =>
    paddockId
      ? state.insightsReducer.farmDiagnosticsOverrides[paddockId]
      : undefined
  );
  const farmYearInputsForFarm = useSelector((state: RootState) =>
    farmId ? state.insightsReducer.farmYearInputs[farmId] : undefined
  );

  return useMemo(() => {
    const years: FarmDiagnosticsYearSeries[] = [];

    for (const row of rows) {
      const category = selectedCategory[row.year] ?? row.defaultCategory;
      const base = category === InputTaskCategory.Actual ? row.actual : row.plan;
      if (!base) continue;

      const override = overridesForPaddock?.[row.year];
      const yieldValue = override?.yieldOverride ?? base.yieldValue;

      const costsPerHa: Partial<Record<SummaryEntityCategory, number>> = {};
      let variableCostPerHa = 0;
      // The full fixed category list, not just Object.keys(base.costsTotal) —
      // the override editor lets a user set a cost/what-if on a category
      // Epic 1 recorded $0 for that year, and that override must still take
      // effect here rather than being silently dropped.
      for (const category of FARM_DIAGNOSTICS_COST_CATEGORY_ORDER) {
        const baseCostTotal = base.costsTotal[category] ?? 0;
        const baseCostPerHa =
          base.areaHa > 0 ? baseCostTotal / base.areaHa : 0;
        const overriddenTotal = override?.costOverrides?.[category];
        const effectivePerHa =
          overriddenTotal != null
            ? base.areaHa > 0
              ? overriddenTotal / base.areaHa
              : 0
            : baseCostPerHa;

        costsPerHa[category] = effectivePerHa;
        variableCostPerHa += effectivePerHa;
      }

      const gmPerHa = computeGrossMarginPerHa(
        yieldValue,
        base.pricePerUnit,
        variableCostPerHa
      );

      const farmYear = farmYearInputsForFarm?.[row.year];
      const rainfallMm = farmYear?.rainfallMm ?? null;
      // Map raw PracticeTag enum keys (e.g. "conventionalTill") to their
      // display labels ("Conventional till") once, here, so every consumer
      // of the series (chart markers, tooltips) shows user-friendly text
      // without each having to know about FARM_DIAGNOSTICS_PRACTICE_LABELS.
      const practiceTags = (farmYear?.practiceTags ?? []).map(
        (tag) => FARM_DIAGNOSTICS_PRACTICE_LABELS[tag]
      );

      const yieldKgPerHa = resolveYieldKgPerHa(
        yieldValue,
        base.yieldUnit,
        gUnits
      );
      const wueIndex =
        yieldKgPerHa != null ? computeWueIndex(yieldKgPerHa, rainfallMm) : null;
      const financialWue = computeFinancialWue(gmPerHa, rainfallMm);

      years.push({
        year: row.year,
        areaHa: base.areaHa,
        yieldValue,
        yieldUnit: base.yieldUnit,
        pricePerUnit: base.pricePerUnit,
        costsPerHa,
        variableCostPerHa,
        gmPerHa,
        rainfallMm,
        practiceTags,
        yieldKgPerHa,
        wueIndex,
        financialWue,
      });
    }

    const wueRollingAvg = computeRollingAverage3yr(
      years.map((y) => ({ year: y.year, value: y.wueIndex }))
    );
    const financialWueRollingAvg = computeRollingAverage3yr(
      years.map((y) => ({ year: y.year, value: y.financialWue }))
    );

    const wueTrendByYear: Record<number, TrendDirection | null> = {};
    const financialWueTrendByYear: Record<number, TrendDirection | null> = {};
    for (const { year } of years) {
      const current = wueRollingAvg.find((p) => p.year === year)?.rollingAvg ?? null;
      const prev =
        wueRollingAvg.find((p) => p.year === year - 1)?.rollingAvg ?? null;
      wueTrendByYear[year] = classifyTrend(current, prev);

      const currentF =
        financialWueRollingAvg.find((p) => p.year === year)?.rollingAvg ?? null;
      const prevF =
        financialWueRollingAvg.find((p) => p.year === year - 1)?.rollingAvg ??
        null;
      financialWueTrendByYear[year] = classifyTrend(currentF, prevF);
    }

    return {
      years,
      wueRollingAvg,
      financialWueRollingAvg,
      wueTrendByYear,
      financialWueTrendByYear,
    };
  }, [rows, overridesForPaddock, farmYearInputsForFarm, gUnits, selectedCategory]);
};
